(ns io.daskul.tutorial.webapp.components.branch-picker
  "Workspace switcher — combined school name + branch picker (F-041).
   Shows school name with branch subtitle. Clicking opens branch menu
   when user has 2+ branches. Otherwise just displays school + branch info."
  (:require
   [clojure.string :as str]
   [uix.core :refer [defui $ use-callback use-memo]]
   [uix.re-frame :as urf]
   [re-frame.core :as rf]
   [io.daskul.tutorial.webapp.domain.active-context.events :as ac.events]
   [io.daskul.tutorial.webapp.domain.active-context.subs :as ac.subs]
   [io.daskul.tutorial.webapp.domain.auth.subs :as auth.subs]
   [io.daskul.tutorial.webapp.app.events :as app.events]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]))

(defn- school-initials
  "Extract initials from school name — first letter of each word, max 2."
  [name]
  (when (seq name)
    (->> (str/split name #"\s+")
         (remove str/blank?)
         (map #(subs % 0 1))
         (take 2)
         (str/join)
         str/upper-case)))

(defn- all-branches-label
  "Role-aware label for the nil-branch (All Branches) option. A branch-admin
   sees 'All My Branches' — honest that it's only their assigned branches, not
   the whole school (SMS-279); a school-admin sees 'All Branches'."
  [role]
  (if (= "branch-admin" role) "All My Branches" "All Branches"))

(defn- current-branch-label
  "Resolve the display label for the current branch selection."
  [contexts branch-id role]
  (if branch-id
    (some (fn [c] (when (= (:branch-id c) branch-id) (:org-unit-name c)))
          contexts)
    (all-branches-label role)))

(defui workspace-switcher []
  (let [school-name (urf/use-subscribe [::app.events/school-name])
        contexts (urf/use-subscribe [::ac.subs/available-contexts])
        current-branch-id (urf/use-subscribe [::ac.subs/branch-id])
        role (urf/use-subscribe [::auth.subs/role])
        real-branches (filterv :branch-id contexts)
        has-branches? (> (count real-branches) 1)
        ;; Single branch: auto-resolve label from the only branch
        branch-label (if (and (= 1 (count real-branches)) (nil? current-branch-id))
                       (:org-unit-name (first real-branches))
                       (current-branch-label contexts current-branch-id role))
        on-select (use-callback
                   (fn [val]
                     (let [branch-id (when (seq val) val)]
                       ;; dispatch-SYNC so ::select-branch persists the new
                       ;; active-context to localStorage *before* the reload.
                       ;; A plain (async) dispatch races the synchronous reload:
                       ;; if the reload wins, the write never runs and the branch
                       ;; switch is silently lost (page reloads, stays on the old
                       ;; branch). :localstorage/set is a synchronous setItem, so
                       ;; dispatch-sync fully commits it first.
                       (rf/dispatch-sync [::ac.events/select-branch branch-id])
                       (.reload js/window.location)))
                   [])
        initials (use-memo #(school-initials school-name) [school-name])
        trigger ($ ui/unstyled-button
                  {:style #js {:borderRadius 6
                               :padding "4px 8px"
                               :cursor (if has-branches? "pointer" "default")}
                   :className "workspace-trigger"}
                  ($ ui/group {:gap "sm" :wrap "nowrap"}
                    ($ ui/box
                      {:style #js {:width 32 :height 32 :borderRadius 8
                                   :background "var(--brand-600)"
                                   :display "flex" :alignItems "center"
                                   :justifyContent "center" :flexShrink 0}}
                      ($ ui/text {:size "xs" :fw 700 :c "white" :lh 1}
                        (or initials "...")))
                    ($ ui/box
                      ($ ui/text {:size "md" :fw 700 :lh 1.3}
                        (or school-name "Loading..."))
                      (when branch-label
                        ($ ui/text {:size "sm" :c "dimmed" :lh 1.3}
                          branch-label)))
                    (when has-branches?
                      ($ icons/icon-chevron-down {:size 14 :color "var(--mantine-color-dimmed)"}))))]
    (if has-branches?
      ($ ui/menu {:shadow "md" :width 220 :position "bottom-start"}
        ($ ui/menu-target trigger)
        ($ ui/menu-dropdown
          (for [ctx contexts]
            (let [val (or (:branch-id ctx) "")
                  label (if (:branch-id ctx) (:org-unit-name ctx) (all-branches-label role))
                  selected? (= val (or current-branch-id ""))]
              ($ ui/menu-item
                (cond-> {:key val :onClick #(on-select val)}
                  selected? (assoc :fw 600 :c "var(--brand-600)"))
                label)))))
      trigger)))

;; Keep old name as alias for backwards compat in core.cljs
(def branch-picker workspace-switcher)
